import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Divider,
} from '@mui/material';
import { CreditCard, Email, CheckCircle } from '@mui/icons-material';

interface PaymentFormProps {
  amount: string;
  serviceName: string;
  serviceType?: string; // "fmla" | "fmla-std" | "follow-up"
  onPaymentSuccess?: () => void;
  onPaymentCancel?: () => void;
  userData: {
    firstName: string;
    lastName: string;
    email: string;
    employmentState: string;
  };
}

const PRE_LAUNCH_MODE = true;

const PaymentForm: React.FC<PaymentFormProps> = ({
  amount,
  serviceName,
  serviceType = 'fmla',
  onPaymentSuccess,
  onPaymentCancel,
  userData
}) => {
  const [loading, setLoading] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(!PRE_LAUNCH_MODE);
  const [hasProviders, setHasProviders] = useState<boolean | null>(PRE_LAUNCH_MODE ? false : null);
  const [providerCount, setProviderCount] = useState(0);
  const [error, setError] = useState('');
  const [showWaitlist, setShowWaitlist] = useState(PRE_LAUNCH_MODE);
  const [waitlistEmail, setWaitlistEmail] = useState(userData.email);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);

  React.useEffect(() => {
    const checkAvailability = async () => {
      if (!userData.employmentState) {
        setCheckingAvailability(false);
        return;
      }
      try {
        const response = await fetch(
          `https://api.fmla.doctor/provider/availability?state=${userData.employmentState}`
        );
        if (response.ok) {
          const data = await response.json();
          setHasProviders(data.hasProviders);
          setProviderCount(data.providerCount || 0);
          if (!data.hasProviders) {
            setShowWaitlist(true);
          }
        }
      } catch (err) {
        setError('Unable to check provider availability. Please try again.');
      } finally {
        setCheckingAvailability(false);
      }
    };
    checkAvailability();
  }, [userData.employmentState]);

  const handlePayment = async () => {
    try {
      setLoading(true);
      setError('');

      // Call backend Stripe endpoint directly
      const response = await fetch(`https://api.fmla.doctor/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // No authorization needed for checkout session creation
        },
        body: JSON.stringify({
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          employmentState: userData.employmentState,
          serviceType: serviceType, // Use passed service type
          // Redirect back to the patient dashboard after payment
          successUrl: `${window.location.origin}/dashboard/booking?payment=success&state=${userData.employmentState}`,
          cancelUrl: `${window.location.origin}/patient/dashboard?payment=cancelled`,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Check if this is a state availability error
        if (response.status === 400 && errorData.message && 
            errorData.message.includes("don't currently have providers")) {
          setShowWaitlist(true);
          setError('');
          return;
        }
        
        throw new Error(errorData.message || 'Failed to create payment session');
      }

      const { url } = await response.json();
      
      // Redirect to Stripe checkout
      window.location.href = url;
    } catch (err: any) {
      setError(err.message || 'Payment setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleWaitlistSignup = async () => {
    try {
      setWaitlistLoading(true);
      setError('');

      const response = await fetch(`https://api.fmla.doctor/provider/waitlist/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          state: userData.employmentState,
          email: waitlistEmail,
          name: `${userData.firstName} ${userData.lastName}`,
          serviceType: serviceType,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to join waitlist');
      }

      setWaitlistSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to join waitlist. Please try again.');
    } finally {
      setWaitlistLoading(false);
    }
  };

  if (waitlistSuccess) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          
          <Typography variant="h5" gutterBottom>
            You're on the Waitlist!
          </Typography>
          
          <Typography variant="body1" color="text.secondary" gutterBottom>
            We'll notify you at <strong>{waitlistEmail}</strong> as soon as appointments are available{userData.employmentState ? ` in ${userData.employmentState}` : ''}.
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            We're onboarding licensed providers now and will reach out as soon as your state is live.
          </Typography>

          <Button
            variant="outlined"
            onClick={onPaymentCancel}
            sx={{ mt: 3 }}
          >
            Back
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (showWaitlist) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Email sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          
          <Typography variant="h5" gutterBottom>
            {PRE_LAUNCH_MODE ? 'Coming Soon' : `We're Not in ${userData.employmentState} Yet`}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {PRE_LAUNCH_MODE
              ? 'We\'re currently onboarding licensed providers and will be accepting appointments soon.'
              : `We don't currently have licensed providers in ${userData.employmentState}, but we're actively recruiting!`}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Join our waitlist to be the first to know when appointments open up.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={waitlistEmail}
            onChange={(e) => setWaitlistEmail(e.target.value)}
            sx={{ mb: 3, maxWidth: 400 }}
            disabled={waitlistLoading}
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={onPaymentCancel}
              disabled={waitlistLoading}
            >
              Back
            </Button>
            
            <Button
              variant="contained"
              onClick={handleWaitlistSignup}
              disabled={waitlistLoading || !waitlistEmail}
              startIcon={waitlistLoading ? <CircularProgress size={20} /> : <Email />}
              sx={{ minWidth: 160 }}
            >
              {waitlistLoading ? 'Joining...' : 'Join Waitlist'}
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
            We'll email you as soon as providers are available in your state
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (checkingAvailability) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Checking provider availability in {userData.employmentState}...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent sx={{ textAlign: 'center', py: 4 }}>
        <CreditCard sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
        
        <Typography variant="h5" gutterBottom>
          Payment Required
        </Typography>
        
        <Typography variant="body1" color="text.secondary" gutterBottom>
          {serviceName}
        </Typography>
        
        <Typography variant="h4" color="primary.main" gutterBottom sx={{ my: 3 }}>
          {amount}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Payment for: {userData.firstName} {userData.lastName}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            onClick={onPaymentCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          
          <Button
            variant="contained"
            onClick={handlePayment}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CreditCard />}
            sx={{ minWidth: 140 }}
          >
            {loading ? 'Processing...' : `Pay ${amount}`}
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Secure payment powered by Stripe
        </Typography>
      </CardContent>
    </Card>
  );
};

export default PaymentForm;